import React, { useState, useCallback, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence, wrap } from "framer-motion";
import tvOverlay from '../assets/tv-overlay.svg';
import { isMobile } from '../lib/deviceUtils';
import ShaderCanvas from './shaders/ShaderCanvas';
import FragmentShader from './shaders/FragmentShader';

export interface MediaItem {
    id: string;
    type: 'image' | 'video';
    src: string;
    fullSrc?: string;
    alt?: string;
    width?: number;
    height?: number;
}

export interface VintageTVProps {
    media?: MediaItem[];
    aspectRatio?: string;
    width?: string;
    height?: string;
    autoPlay?: boolean;
    autoPlayInterval?: number;
    transitionDuration?: number;
    showOverlay?: boolean;
    showStatic?: boolean;
}

const VintageTV = ({ 
    media = [],
    aspectRatio = '9/7', 
    width = '100%', 
    height = '100%',
    autoPlay = false,
    autoPlayInterval = 3000,
    transitionDuration = 400,
    showOverlay = true,
    showStatic
}: VintageTVProps) => {
    const [[currentIndex, direction], setPage] = useState([0, 0]);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const isTransitioningRef = useRef(false);
    const mediaCount = media.length;
    const hasContent = mediaCount > 0;
    const mediaIndex = hasContent ? wrap(0, mediaCount, currentIndex) : 0;
    const currentMedia = hasContent ? media[mediaIndex] : null;

    const paginate = useCallback((newDirection: number) => {
        if (isTransitioningRef.current) return;
        
        isTransitioningRef.current = true;
        setIsTransitioning(true);
        setPage(([current]) => [current + newDirection, newDirection]);
        
        // Reset transition state after animation completes
        setTimeout(() => {
            isTransitioningRef.current = false;
            setIsTransitioning(false);
        }, transitionDuration);
    }, [transitionDuration]);

    React.useEffect(() => {
        if (!autoPlay || mediaCount <= 1) return;
        
        const timer = setInterval(() => {
            paginate(1);
        }, autoPlayInterval);

        return () => clearInterval(timer);
    }, [autoPlay, autoPlayInterval, mediaCount, paginate]);

    // On mobile, disable shader to prevent WebGL crashes
    const shouldShowShader = showStatic !== undefined ? showStatic : !isMobile();
    // When showStatic is explicitly true, show static at full opacity
    // Otherwise, show static during transitions or when no content
    const staticVisible = showStatic !== undefined 
        ? showStatic 
        : (!hasContent || isTransitioning);
    
    return (
        <Container aspectRatio={aspectRatio} width={width} height={height}>
            <MaskedContent>
                {/* Three.js TV static shader - disabled on mobile */}
                {shouldShowShader && (
                    <StaticOverlay $isVisible={staticVisible}>
                        <ShaderCanvas>
                            <FragmentShader 
                                fragmentShader={tvStaticShader}
                                transparent={false}
                            />
                        </ShaderCanvas>
                    </StaticOverlay>
                )}

                {/* Media content - only render when content exists AND not showing forced static */}
                {hasContent && showStatic !== true && (
                    <AnimatePresence initial={false} custom={direction}>
                        <MediaSlide
                            key={currentIndex}
                            custom={direction}
                            variants={crtTransitionVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{}}
                        >
                            {currentMedia && (
                                currentMedia.type === 'video' ? (
                                <video
                                    src={currentMedia.src}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover',
                                        filter: 'saturate(0)',
                                        pointerEvents: 'none'
                                    }}
                                />
                            ) : (
                                <img 
                                    src={currentMedia.src} 
                                    alt={currentMedia.alt || "TV content"}
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover',
                                        pointerEvents: 'none',
                                        userSelect: 'none'
                                    }} 
                                    draggable={false}
                                />
                            ))}
                        </MediaSlide>
                    </AnimatePresence>
                )}
            </MaskedContent>
            {showOverlay && <Overlay src={tvOverlay} alt="" />}
        </Container>
    );
};

export default VintageTV;

// MARK: - TV Static Shader

const tvStaticShader = `
uniform float u_time;
uniform vec2 u_resolution;
varying vec2 vUv;

// 3D hash function for better noise
float hash(vec3 p) {
    p = fract(p * vec3(443.897, 441.423, 437.195));
    p += dot(p, p.yzx + 19.19);
    return fract((p.x + p.y) * p.z);
}

// 3D noise function with time in z-axis
float noise3D(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    
    // Cubic interpolation for smoother noise
    vec3 u = f * f * (3.0 - 2.0 * f);
    
    // Sample 8 corners of the cube
    float a = hash(i);
    float b = hash(i + vec3(1.0, 0.0, 0.0));
    float c = hash(i + vec3(0.0, 1.0, 0.0));
    float d = hash(i + vec3(1.0, 1.0, 0.0));
    float e = hash(i + vec3(0.0, 0.0, 1.0));
    float f1 = hash(i + vec3(1.0, 0.0, 1.0));
    float g = hash(i + vec3(0.0, 1.0, 1.0));
    float h = hash(i + vec3(1.0, 1.0, 1.0));
    
    // Trilinear interpolation
    return mix(
        mix(mix(a, b, u.x), mix(c, d, u.x), u.y),
        mix(mix(e, f1, u.x), mix(g, h, u.x), u.y),
        u.z
    );
}

void main() {
    vec2 uv = vUv;
    
    // Use time as z-axis for animated 3D noise
    float t = u_time * 15.0;
    
    // Much denser noise coordinates
    vec3 noiseCoord = vec3(uv * 250.0, t);
    
    // Multiple layers of 3D noise with different frequencies and time speeds
    float n1 = noise3D(noiseCoord);
    float n2 = noise3D(noiseCoord * 2.3 + vec3(0.0, 0.0, t * 0.7));
    float n3 = noise3D(noiseCoord * 4.7 - vec3(0.0, 0.0, t * 0.5));
    float n4 = noise3D(noiseCoord * 8.1 + vec3(0.0, 0.0, t * 1.2));
    
    // Combine layers for denser, more chaotic static
    float staticNoise = (n1 + n2 * 0.6 + n3 * 0.4 + n4 * 0.3) / 2.3;
    
    // Add dense horizontal scan lines
    float scanline = sin(uv.y * u_resolution.y * 2.0) * 0.05;
    staticNoise += scanline;
    
    // More frequent bright flashes
    float flash = step(0.96, hash(vec3(floor(t * 3.0), 0.0, 0.0)));
    staticNoise = mix(staticNoise, 1.0, flash * 0.4);
    
    // Slight blue tint for authentic CRT feel
    vec3 color = vec3(staticNoise * 0.9, staticNoise * 0.95, staticNoise);
    
    gl_FragColor = vec4(color, 1.0);
}
`;

// MARK: - Animation Variants

const crtTransitionVariants = {
    enter: {
        opacity: 0,
        scale: 1.0,
        filter: "brightness(0.2)",
    },
    center: {
        opacity: 1,
        scale: 1,
        scaleY: 1,
        filter: "brightness(1)",
    },
    exit: {
        opacity: 0,
        scale: 1.0,
        filter: "brightness(0.2)",
    }
};

// MARK: - Styled Components

const Container = styled.div<{ 
    aspectRatio: string; 
    width: string; 
    height: string; 
}>`
    position: relative;
    aspect-ratio: ${({ aspectRatio }) => aspectRatio};
    width: ${({ width }) => width};
    height: ${({ height }) => height};
`;

export const MaskedContent = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    mask-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 340 263' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M307.85 205.162C307.85 205.162 307.747 205.431 307.602 206.258C305.496 218.647 294.117 220.405 294.117 220.405C257.77 227.396 175.804 228.741 175.804 228.741H172.582H163.496H161.575C146.438 228.41 77.0276 227.52 43.4069 221.378C43.4069 221.378 31.842 219.475 29.3019 206.113C29.1367 206.217 29.1573 205.162 29.1573 205.162C21.826 143.651 25.8118 80.6704 28.8475 54.9408C28.9714 52.6243 30.7888 42.3862 31.5116 40.0904C31.5322 39.9456 31.5529 39.8836 31.5529 39.8836L30.954 41.745C33.6594 30.0385 42.7667 29.7075 42.7667 29.7075C76.9863 22.7994 154.905 21.1448 162.628 21H162.649H176.465L176.527 21.062C191.334 21.393 262.086 23.2338 294.261 29.7282C294.261 29.7282 303.265 31.8172 305.434 39.9042H305.475C305.475 39.9042 305.475 39.987 305.517 40.1318C306.239 42.4276 307.355 52.2727 307.478 54.5892C310.514 80.3188 315.223 143.713 307.891 205.224L307.85 205.162Z' fill='white'/%3E%3C/svg%3E");
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 340 263' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M307.85 205.162C307.85 205.162 307.747 205.431 307.602 206.258C305.496 218.647 294.117 220.405 294.117 220.405C257.77 227.396 175.804 228.741 175.804 228.741H172.582H163.496H161.575C146.438 228.41 77.0276 227.52 43.4069 221.378C43.4069 221.378 31.842 219.475 29.3019 206.113C29.1367 206.217 29.1573 205.162 29.1573 205.162C21.826 143.651 25.8118 80.6704 28.8475 54.9408C28.9714 52.6243 30.7888 42.3862 31.5116 40.0904C31.5322 39.9456 31.5529 39.8836 31.5529 39.8836L30.954 41.745C33.6594 30.0385 42.7667 29.7075 42.7667 29.7075C76.9863 22.7994 154.905 21.1448 162.628 21H162.649H176.465L176.527 21.062C191.334 21.393 262.086 23.2338 294.261 29.7282C294.261 29.7282 303.265 31.8172 305.434 39.9042H305.475C305.475 39.9042 305.475 39.987 305.517 40.1318C306.239 42.4276 307.355 52.2727 307.478 54.5892C310.514 80.3188 315.223 143.713 307.891 205.224L307.85 205.162Z' fill='white'/%3E%3C/svg%3E");
    -webkit-mask-size: 100% 100%;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
`;

const StaticOverlay = styled.div<{ $isVisible: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    opacity: ${({ $isVisible }) => $isVisible ? 1 : 0.1};
    pointer-events: none;
    transition: opacity 0.3s ease;
    will-change: opacity;
        
    /* Ensure children inherit dimensions */
    > * {
        width: 100%;
        height: 100%;
    }
`;

const MediaSlide = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color:rgb(148, 148, 148);
`;

const Overlay = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.7;
    mix-blend-mode: luminosity;
    pointer-events: none;
`;

export { TVMaskedContent } from './TVMask';
